import { Record } from "appworks/model/gameplay/records/record";
import { Result } from "appworks/model/gameplay/records/results/result";
import { ResultBuilder } from "appworks/server/builders/result-builder";
import { shuffle } from "appworks/utils/collection-utils";
import { EggColor, FowlPickBonusResult } from "model/results/fowl-pick-bonus-result";
import { SlingoChanceGameSchema, SlingoResponse } from "slingo/integration/slingo-schema";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";

export class FowlPickBonusResultBuilder implements ResultBuilder {
    public filter(data: SlingoResponse, record?: Record, additionalData?: any): boolean {
        return Boolean(data.spinResult?.feature?.prize ?? additionalData === "scatter");
    }

    public build(record: Record, data: SlingoResponse, additionalData?: any): Result[] {
        if (additionalData === "scatter") {
            return this.buildSlotBonusResult(record, data, additionalData);
        } else {
            return this.buildBaseGameResult(record, data, additionalData);
        }
    }

    protected buildBaseGameResult(record: Record, data: SlingoResponse, additionalData?: any): Result[] {
        const featureData = data.spinResult.feature.prize as SlingoChanceGameSchema<{ eggTypes: EggColor[]; missedEggTypes: EggColor[]; misses: number[] }>;

        const pickBonusResult = new FowlPickBonusResult("eggpick");
        pickBonusResult.chickenPositions = this.getChickenPositions(record, data, featureData);
        pickBonusResult.bonusVariant = featureData.gameConfig.id.includes("scatter") ? "single_tier" : "three_tier";

        const results: Result[] = [pickBonusResult];

        // Wins
        featureData.payout.forEach((value, index) => {
            pickBonusResult.hits.push({
                color: featureData.params.eggTypes[index],
                value: value * 100,
            });
            pickBonusResult.cashWon += value * 100;
        });

        // Misses
        featureData.params.misses.forEach((value, index) => {
            pickBonusResult.misses.push({
                color: featureData.params.missedEggTypes[index],
                value: value * 100,
            });
        });

        // Jackpot Egg
        if (featureData.games) {
            pickBonusResult.jackpotEgg = {
                color: "fox",
                value: 0,
            };
        }

        return results;
    }

    // The response for slot picks is better than it was but still pretty messy compared to a regular pick
    protected buildSlotBonusResult(record: Record, data: any, additionalData?: any): Result[] {
        const pickBonusResult = new FowlPickBonusResult("eggpick");
        pickBonusResult.chickenPositions = this.getChickenPositions(record, null, null);
        pickBonusResult.bonusVariant = "single_tier";

        const results: Result[] = [pickBonusResult];

        // Wins
        data.feature.chanceGamePayout[0].wins.forEach((value: number, index: number) => {
            pickBonusResult.hits.push({
                color: data.feature.chanceGameFeature.params.eggTypes[index],
                value: value * 100,
            });
            pickBonusResult.cashWon += value * 100;
        });

        // Misses
        data.feature.chanceGameFeature.params.misses.forEach((value: number, index: number) => {
            pickBonusResult.misses.push({
                color: data.feature.chanceGameFeature.params.missedEggTypes[index],
                value: value * 100,
            });
        });

        // Jackpot Egg
        if (data.feature.jackpot?.length > 0) {
            pickBonusResult.jackpotEgg = {
                color: "fox",
                value: 0,
            };
        }

        return results;
    }

    protected getChickenPositions(record: Record, responseData: SlingoResponse, chanceGame: SlingoChanceGameSchema): number[] {
        if (chanceGame?.gameConfig.id.includes("scatter")) {
            // Base game scatter
            const scatterIndexes = responseData.spinResult.reelSequence.symbols
                .map((sym, index) => (sym === "P" ? index : null))
                .filter(symIndex => symIndex !== null);
            return scatterIndexes;
        } else if (record instanceof SpinRecord) {
            // Slot scatter
            const scatterIndexes = record.grid.map((reel, index) => (reel.indexOf("slotbonus_SC") !== -1 ? index : null)).filter(symIndex => symIndex !== null);
            return scatterIndexes;
        } else {
            // end game 3-tier pick
            const shuffledPositions = shuffle([0, 1, 2, 3, 4, 5]);
            shuffledPositions.length = chanceGame.payout.length + chanceGame.params.misses.length;
            return shuffledPositions;
        }
    }
}
